import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { getIncidentLocations } from '../../AlertsGL/utils';
import { createWebMercatorViewport } from '../../utils';
import MemoizedAlertPopover from './AlertPopover';

const AlertMapPopover = ({ overlay }) => {
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [popoverData, setPopoverData] = useState(null);
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);
  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const alertPopover = useSelector(state => state.mapPopover.alertPopover);
  const alerts = useSelector(state => state.incidents.alerts);
  const hoveredAlertID = useSelector(
    state => state.hoveredIncident.hoveredAlertID,
  );

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const isAllView = useMemo(
    () => !selectedAlertID && !selectedCallerID,
    [selectedAlertID, selectedCallerID],
  );

  const incidentPopoverNormalized = useMemo(() => {
    if (alertPopover) {
      return alertPopover;
    } else {
      const isAlertHovered = !!alerts[hoveredAlertID];
      if (isAlertHovered) {
        if (!selectedAlertID || selectedAlertID === hoveredAlertID) {
          const incidentLocation = getIncidentLocations(
            alerts,
            hoveredAlertID,
          )[0];
          return incidentLocation;
        }
      } else {
        return null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertPopover, hoveredAlertID]);

  /**
   * Repositions the popover based on the current viewState.
   * @param {Object} viewState
   */
  const reposition = useCallback(
    (viewState, incidentPopoverNormalized) => {
      const viewport = createWebMercatorViewport(viewState, basemap);
      if (incidentPopoverNormalized) {
        let incidentLocation = [
          incidentPopoverNormalized.longitude,
          incidentPopoverNormalized.latitude,
        ];
        const [x, y] = viewport.project(incidentLocation);
        setPopoverData({
          data: incidentPopoverNormalized,
          x,
          y,
        });
        setPopoverPosition({ x, y });
      }
    },
    [setPopoverPosition, basemap],
  );

  // Reposition whenever the popover data changes.
  useEffect(() => {
    if (overlay && incidentPopoverNormalized) {
      const viewState = overlay.getViewState();
      reposition(viewState, incidentPopoverNormalized);
    }

    if (!incidentPopoverNormalized) {
      setPopoverPosition(null);
      setPopoverData(null);
    }
  }, [overlay, incidentPopoverNormalized, reposition]);

  return (
    <>
      {mapProvider === GOOGLE && popoverPosition && popoverData && (
        <MemoizedAlertPopover
          x={popoverPosition?.x}
          y={popoverPosition?.y}
          data={popoverData?.data}
          isAllView={isAllView}
        />
      )}
    </>
  );
};

export default AlertMapPopover;
